import { getRailsClient } from "sj-ui-toolkit/src/client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ActivityObject {
  id: number;
  name: string;
}

// ---------------------------------------------------------------------------
// Shared Activity Types
// ---------------------------------------------------------------------------

export type MentorRequestStatus = "open" | "closed";
export type EvaluateRequestStatus =
  | "submitted"
  | "in_progress"
  | "failed"
  | "passed"
  | "completed"
  | "needs_more_information";
export type RequestStatus = MentorRequestStatus | EvaluateRequestStatus;

/** Shape used by both list pages (MentorRequests + EvaluateRequests). */
export interface ActivityData {
  id: number;
  text: string;
  description?: string;
  createdAt: string;
  userName: string;
  requestStatus: RequestStatus;
}

// ---------------------------------------------------------------------------
// Mentor Requests Types & API
// ---------------------------------------------------------------------------

export interface MentorRequestEntry {
  dojoActivityId: number;
  createdAt: string;
  userName: string;
}

interface MentorRequestsResponse {
  mentorRequests: MentorRequestEntry[];
}

export const fetchMentorRequests = async (params: {
  assigned_to: string;
  order: string;
  status: MentorRequestStatus;
}): Promise<MentorRequestEntry[]> => {
  const API = `/api/internal_api/v1/admin/activities/mentor_requests`;
  const { data } = await getRailsClient().get<MentorRequestsResponse>(API, { params });
  return data.mentorRequests ?? [];
};

// ---------------------------------------------------------------------------
// Evaluate Requests Types & API
// ---------------------------------------------------------------------------

export interface EvaluateRequestEntry {
  id: number;
  name: string;
  status: EvaluateRequestStatus;
  createdAt: string;
  updatedAt: string;
}

interface EvaluateRequestsResponse {
  dojoActivities: EvaluateRequestEntry[];
}

export const fetchEvaluateRequests = async (params: {
  assigned_to: string;
  order: string;
  status: EvaluateRequestStatus;
}): Promise<EvaluateRequestEntry[]> => {
  const API = `/api/internal_api/v1/admin/activities/judgment_requests`;
  const { data } = await getRailsClient().get<EvaluateRequestsResponse>(API, { params });
  return data.dojoActivities ?? [];
};

// ---------------------------------------------------------------------------
// Dojo Activity (shared detail fetch for both flows)
// ---------------------------------------------------------------------------

interface DojoActivityApiData {
  id: number;
  text: string;
  description?: string;
  [key: string]: unknown;
}

interface DojoActivityResponse {
  activity: DojoActivityApiData;
}

export const fetchDojoActivity = async (
  dojoActivityId: number,
  statusType: "mentor_request" | "judgment_request" = "mentor_request"
): Promise<DojoActivityApiData | null> => {
  const API = `/api/internal_api/v1/admin/dojo_activities/${dojoActivityId}`;
  try {
    const { data } = await getRailsClient().get<DojoActivityResponse>(API, {
      params: { status: statusType },
    });
    return data.activity;
  } catch (err) {
    console.error(`Failed to load activity ${dojoActivityId}:`, err);
    return null;
  }
};

// ---------------------------------------------------------------------------
// Activity Detail Types & API
// ---------------------------------------------------------------------------

export interface ActivityDetailUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

export interface ActivityNote {
  id: number;
  text: string;
  title?: string;
  userName: string;
  humanCreatedAt: string;
  fromAdmin: boolean;
}

export interface HistoricalActivity {
  id: number;
  status: string;
  createdAt?: string;
  updatedAt?: string;
  lastActivity?: number;
}

export interface ActivityDetailData {
  id: number;
  text: string;
  description?: string;
  points: number;
  status: string;
  requestStatus?: RequestStatus;
  lastActivity?: number;
  user?: ActivityDetailUser;
  createdAt?: string;
}

interface ActivityDetailResponse {
  activity: ActivityDetailData;
  notes: ActivityNote[];
  historicalActivities: HistoricalActivity[];
}

export const fetchActivityDetail = async (
  dojoActivityId: string,
  statusType: "mentor_request" | "judgment_request"
): Promise<ActivityDetailResponse> => {
  const API = `/api/internal_api/v1/admin/dojo_activities/${dojoActivityId}`;
  const { data } = await getRailsClient().get<ActivityDetailResponse>(API, {
    params: { status: statusType },
  });
  return data;
};

export const fetchActivityNotes = async (
  activityId: number
): Promise<{ activityNotes: ActivityNote[] }> => {
  const API = `/api/internal_api/v1/dojo_activities/${activityId}/activity_notes`;
  const { data } = await getRailsClient().get<{ activityNotes: ActivityNote[] }>(API);
  return data;
};

export const addMentorNote = async (activityNoteId: number, text: string): Promise<void> => {
  const API = `/api/internal_api/v1/admin/activities/mentor_requests`;
  await getRailsClient().post(API, {
    mentor_request: { activity_note_id: activityNoteId, just_message: true, text },
  });
};

export const closeMentorRequest = async (activityNoteId: number): Promise<void> => {
  const API = `/api/internal_api/v1/admin/activities/mentor_requests`;
  await getRailsClient().post(API, {
    mentor_request: {
      activity_note_id: activityNoteId,
      just_message: false,
      text: "Closed mentor request",
    },
  });
};

export const addEvaluateNote = async (evaluateRequestId: number, text: string): Promise<void> => {
  const API = `/api/internal_api/v1/admin/activities/judgment_requests/${evaluateRequestId}`;
  await getRailsClient().put(API, { text });
};

export const setEvaluateStatus = async (
  evaluateRequestId: number,
  status: "passed" | "failed" | "needs_more_information"
): Promise<void> => {
  const API = `/api/internal_api/v1/admin/activities/judgment_requests/${evaluateRequestId}`;
  await getRailsClient().put(API, { status });
};

export interface ActivityAction {
  id: number;
  name: string;
  objects: ActivityObject[];
}

export interface ActionsResponse {
  actions: ActivityAction[];
  maxActionId: number;
  maxObjectId: number;
}

export interface Impact {
  id: number;
  name: string;
}

export interface ImpactsResponse {
  impacts: Impact[];
  maxImpactId: number;
}

export interface MentorGuide {
  id: number;
  name: string;
}

export interface MentorGuidesResponse {
  mentorGuides: MentorGuide[];
  maxMentorGuideId: number;
}

export interface SettingsResponse {
  settings: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export const fetchActions = async (): Promise<ActionsResponse> => {
  const API = `/api/internal_api/v1/admin/activities/actions`;
  const { data } = await getRailsClient().get<ActionsResponse>(API);
  return data;
};

export const saveActions = async (actions: ActivityAction[]): Promise<ActionsResponse> => {
  const API = `/api/internal_api/v1/admin/activities/actions`;
  const { data } = await getRailsClient().post<ActionsResponse>(API, { actions });
  return data;
};

export const restoreActions = async (): Promise<ActionsResponse> => {
  const API = `/api/internal_api/v1/admin/activities/actions/restore`;
  const { data } = await getRailsClient().post<ActionsResponse>(API);
  return data;
};

// ---------------------------------------------------------------------------
// Impacts
// ---------------------------------------------------------------------------

export const fetchImpacts = async (): Promise<ImpactsResponse> => {
  const API = `/api/internal_api/v1/admin/activities/impacts`;
  const { data } = await getRailsClient().get<ImpactsResponse>(API);
  return data;
};

export const saveImpacts = async (impacts: Impact[]): Promise<ImpactsResponse> => {
  const API = `/api/internal_api/v1/admin/activities/impacts`;
  const { data } = await getRailsClient().post<ImpactsResponse>(API, { impacts });
  return data;
};

export const restoreImpacts = async (): Promise<ImpactsResponse> => {
  const API = `/api/internal_api/v1/admin/activities/impacts/restore`;
  const { data } = await getRailsClient().post<ImpactsResponse>(API);
  return data;
};

// ---------------------------------------------------------------------------
// Mentor Guides
// ---------------------------------------------------------------------------

export const fetchMentorGuides = async (): Promise<MentorGuidesResponse> => {
  const API = `/api/internal_api/v1/admin/activities/mentor_guides`;
  const { data } = await getRailsClient().get<MentorGuidesResponse>(API);
  return data;
};

export const saveMentorGuides = async (
  mentorGuides: MentorGuide[]
): Promise<MentorGuidesResponse> => {
  const API = `/api/internal_api/v1/admin/activities/mentor_guides`;
  const { data } = await getRailsClient().post<MentorGuidesResponse>(API, { mentorGuides });
  return data;
};

export const restoreMentorGuides = async (): Promise<MentorGuidesResponse> => {
  const API = `/api/internal_api/v1/admin/activities/mentor_guides/restore`;
  const { data } = await getRailsClient().post<MentorGuidesResponse>(API);
  return data;
};

// ---------------------------------------------------------------------------
// Custom Messaging (settings)
// ---------------------------------------------------------------------------

export const fetchActivityMessageSettings = async (): Promise<SettingsResponse> => {
  const API = `/api/internal_api/v1/admin/settings`;
  const { data } = await getRailsClient().get<SettingsResponse>(API);
  return data;
};

export const saveActivityMessageSetting = async (
  name: string,
  value: string | null
): Promise<void> => {
  const API = `/api/internal_api/v1/admin/settings/set`;
  await getRailsClient().put(API, { setting: { name, value } });
};
